import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  Button,
  Grid,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useSiteStore } from '../../store/site.store';
import { usePillarDetail } from '../../hooks/usePillarDetail';
import { activationService } from '../../services/activationService';

const ReadinessPillarDetail: React.FC = () => {
  const { pillarId } = useParams<{ pillarId: string }>();
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  const { data: pillarDetail, isLoading, error } = usePillarDetail(siteId, pillarId || '');

  // Track pillar detail view
  useEffect(() => {
    if (selectedSite?.id && pillarId && pillarDetail) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService.trackEvent(selectedSite.id, {
        eventType: 'READINESS_PILLAR_VIEWED',
        sessionId,
        metadata: {
          pillarId: pillarId,
          pillarName: pillarDetail.pillarName,
          score: pillarDetail.score,
          viewType: 'detail'
        }
      }).catch(err => console.error('Failed to track pillar detail view:', err));
    }
  }, [selectedSite?.id, pillarId, pillarDetail]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASS':
        return 'success';
      case 'FAIL':
        return 'error';
      case 'INFO':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PASS':
        return <CheckCircleIcon color="success" />;
      case 'FAIL':
        return <ErrorIcon color="error" />;
      case 'INFO':
        return <InfoIcon color="info" />;
      default:
        return null;
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'LOW':
        return 'success';
      case 'MEDIUM':
        return 'warning';
      case 'HIGH':
        return 'error';
      default:
        return 'default';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'LOW':
        return 'default';
      case 'MEDIUM':
        return 'info';
      case 'HIGH':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTrendIcon = (delta?: number | null) => {
    if (delta == null || delta === 0) return <TrendingFlatIcon fontSize="small" />;
    return delta > 0 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />;
  };

  const getTrendColor = (delta?: number | null) => {
    if (delta == null || delta === 0) return 'default';
    return delta > 0 ? 'success' : 'error';
  };

  const trendLabel = (delta?: number | null) => {
    if (delta == null) return 'No prior snapshot';
    if (delta === 0) return 'No score change';
    return `${delta > 0 ? '+' : ''}${delta} since last snapshot`;
  };

  const issueGroups = pillarDetail.issueGroups || [];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load pillar details. Please try again.
      </Alert>
    );
  }

  if (!pillarDetail) {
    return (
      <Alert severity="warning">
        Pillar details not found.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/early-stage')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="h4" component="h1">
            {pillarDetail.pillarName}
          </Typography>
          <Chip
            label={pillarDetail.status}
            color={pillarDetail.status === 'READY' ? 'success' : 'warning'}
          />
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          {pillarDetail.summary}
        </Typography>
      </Box>

      {/* Score Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Overall Score
              </Typography>
              <Typography variant="body2">
                {pillarDetail.whyItMatters}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h2" fontWeight="bold">
                {pillarDetail.score}
              </Typography>
              <Typography variant="body2">/ 100</Typography>
              <Chip
                icon={getTrendIcon(pillarDetail.scoreDelta)}
                label={trendLabel(pillarDetail.scoreDelta)}
                color={getTrendColor(pillarDetail.scoreDelta) as any}
                size="small"
                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.16)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pillar Issue Trends */}
      {(pillarDetail.affectedPageCount || issueGroups.length > 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
              <Typography variant="h6">Issue Groups and Trends</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label={`${pillarDetail.affectedPageCount || 0} affected pages`} size="small" />
                <Chip label={`${pillarDetail.failedCheckCount || 0} failed checks`} color="error" size="small" variant="outlined" />
                <Chip label={`${pillarDetail.warningCheckCount || 0} warnings`} color="warning" size="small" variant="outlined" />
              </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ mb: issueGroups.length > 0 ? 2 : 0 }}>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">New</Typography>
                  <Typography variant="h6">{pillarDetail.newIssueCount || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Recurring</Typography>
                  <Typography variant="h6">{pillarDetail.recurringIssueCount || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Resolved</Typography>
                  <Typography variant="h6">{pillarDetail.resolvedIssueCount || 0}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Worsening</Typography>
                  <Typography variant="h6">{pillarDetail.worseningIssueCount || 0}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {issueGroups.length > 0 && (
              <Grid container spacing={2}>
                {issueGroups.map((group, index) => (
                  <Grid item xs={12} md={6} key={`${group.checkId || 'check'}-${index}`}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {group.checkName || group.checkId || 'Readiness issue'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {group.recommendation || 'Review affected pages and resolve the matching readiness issue.'}
                          </Typography>
                        </Box>
                        <Chip label={`${group.affectedPageCount || 0} pages`} size="small" color="primary" variant="outlined" />
                      </Box>
                      <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                        {group.status && <Chip label={group.status} size="small" color={group.status === 'FAIL' ? 'error' : 'warning'} />}
                        {group.checkId && <Chip label={group.checkId} size="small" variant="outlined" />}
                        {group.actionType && <Chip label={group.actionType} size="small" variant="outlined" />}
                      </Box>
                      {(group.sampleUrls || []).length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Sample affected pages
                          </Typography>
                          {(group.sampleUrls || []).slice(0, 3).map((url) => (
                            <Typography key={url} variant="body2" color="text.secondary" noWrap title={url}>
                              {url}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Educational Resources */}
      <Card sx={{ mb: 3, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon /> Learn More
          </Typography>
          <Typography variant="body2" paragraph>
            Get expert guidance on improving this pillar:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {getEducationalLinks(pillarId || '').map((link, index) => (
              <Link
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (selectedSite?.id) {
                    const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
                    activationService.trackEvent(selectedSite.id, {
                      eventType: 'EDUCATION_LINK_CLICKED',
                      sessionId,
                      metadata: { pillarId, linkTitle: link.title, linkUrl: link.url }
                    }).catch(err => console.error('Failed to track education link click:', err));
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {link.title}
                <OpenInNewIcon fontSize="small" />
              </Link>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Checks Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Checks
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {pillarDetail.checks.map((check, index) => (
              <React.Fragment key={index}>
                <ListItem alignItems="flex-start">
                  <Box display="flex" width="100%" gap={2}>
                    <Box mt={0.5}>{getStatusIcon(check.status)}</Box>
                    <Box flexGrow={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {check.checkName}
                        </Typography>
                        <Chip
                          label={check.status}
                          size="small"
                          color={getStatusColor(check.status) as any}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {check.description}
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          Result: {check.result}
                        </Typography>
                      </Paper>
                      {check.recommendation && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Recommendation:</strong> {check.recommendation}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </Box>
                </ListItem>
                {index < pillarDetail.checks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Actions Section */}
      {pillarDetail.actions && pillarDetail.actions.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recommended Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {pillarDetail.actions.map((action, index) => (
                <Grid item xs={12} key={index}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {action.actionTitle}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={`${action.effortLevel} Effort`}
                          size="small"
                          color={getEffortColor(action.effortLevel) as any}
                        />
                        <Chip
                          label={`${action.impactLevel} Impact`}
                          size="small"
                          color={getImpactColor(action.impactLevel) as any}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {action.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Estimated time: {action.estimatedMinutes} minutes
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// Helper function to get educational links for each pillar
function getEducationalLinks(pillarId: string): { title: string; url: string }[] {
  const links: Record<string, { title: string; url: string }[]> = {
    indexability: [
      { title: 'Google Search Indexing Guide', url: 'https://developers.google.com/search/docs/crawling-indexing/overview' },
      { title: 'How Googlebot Works', url: 'https://developers.google.com/search/docs/crawling-indexing/googlebot' },
      { title: 'Robots.txt Guide', url: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro' },
    ],
    content_intent: [
      { title: 'Understanding Search Intent', url: 'https://developers.google.com/search/docs/fundamentals/creating-helpful-content' },
      { title: 'Content Best Practices', url: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide' },
      { title: 'E-E-A-T Guidelines', url: 'https://developers.google.com/search/docs/fundamentals/creating-helpful-content#trust' },
    ],
    technical_hygiene: [
      { title: 'Core Web Vitals', url: 'https://web.dev/vitals/' },
      { title: 'Mobile-Friendly Test', url: 'https://search.google.com/test/mobile-friendly' },
      { title: 'PageSpeed Insights', url: 'https://pagespeed.web.dev/' },
    ],
    internal_linking: [
      { title: 'Internal Linking Best Practices', url: 'https://developers.google.com/search/docs/crawling-indexing/links-crawlable' },
      { title: 'Site Structure Guide', url: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide#organize' },
      { title: 'Anchor Text Guidelines', url: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide#use-links-wisely' },
    ],
    ai_searchability: [
      { title: 'How to create a llms.txt file', url: 'https://llmstxt.org/' },
      { title: 'Google: Controlling crawling with robots.txt', url: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro' },
      { title: 'Schema.org for Entity Signals (Organization)', url: 'https://schema.org/Organization' },
      { title: 'Perplexity: How AI citation works', url: 'https://www.perplexity.ai/hub/faq/how-does-perplexity-work' },
      { title: 'OpenAI: Web crawling & GPTBot', url: 'https://platform.openai.com/docs/gptbot' },
    ],
  };

  return links[pillarId] || [
    { title: 'Google Search Central', url: 'https://developers.google.com/search' },
    { title: 'SEO Starter Guide', url: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide' },
  ];
}

export default ReadinessPillarDetail;
